/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import { isNonEmptyObject } from "../../utils/is-non-empty-object"
import { valueOrDash } from "../../utils/value-or-dash"

import styles from "./flag-metadata.module.css"

interface Props {
    /**
     * Name of the flag whose metadata is shown.
     *
     * Used to give the scrollable region an accessible name.
     */
    flagName: string | undefined

    /**
     * Parsed metadata written by the flag detector.
     *
     * Empty values at any depth are rendered as an em dash.
     */
    value: unknown
}

/**
 * Renders flag metadata as nested lists.
 *
 * Objects use their keys as labels, arrays use their indexes implicitly, and
 * nested objects and arrays are rendered one level deeper. The metadata is
 * shown in a scrollable region capped at a quarter of the viewport height.
 *
 * The region is focusable so keyboard users can scroll it in Safari, where a
 * scroll container without focusable content cannot receive keyboard focus.
 *
 * @example
 * <FlagMetadata flagName={flag.name} value={flag.metadata} />
 * // unwantedApps:
 * //   name: AnyDesk
 * //   customData:
 * //     packageName: com.anydesk.anydeskandroid
 */
export const FlagMetadata = observer(({ flagName, value }: Props) => (
    <div className={styles.root} role="region" aria-label={`${flagName} metadata`} tabIndex={0}>
        {renderValue(value)}
    </div>
))

/**
 * Renders a metadata value, recursing into nested objects and arrays.
 *
 * Non-empty objects and arrays are rendered as nested lists. Other values stay
 * on the current line, with missing or empty values shown as an em dash.
 *
 * @param value - Metadata value at the current level.
 * @returns Nested list markup, an em dash, or the value as text.
 */
function renderValue(value: unknown) {
    if (isNonEmptyObject(value)) {
        if (Array.isArray(value)) {
            return (
                <ul className={styles.list}>
                    {value.map((item, index) => (
                        // biome-ignore lint/suspicious/noArrayIndexKey: the items have no identity and never reorder
                        <li key={index}>{renderValue(item)}</li>
                    ))}
                </ul>
            )
        }

        return (
            <ul className={styles.list}>
                {Object.entries(value).map(([key, item]) => (
                    <li key={key}>
                        {/* Keeps the key and inline value separated; collapses before a nested list. */}
                        <span className={styles.key}>{key}: </span>
                        {renderValue(item)}
                    </li>
                ))}
            </ul>
        )
    }

    // Treat nullish, blank, and empty objects or arrays as missing values.
    const isEmpty = value == null || value === "" || typeof value === "object"
    return valueOrDash(isEmpty ? null : String(value))
}
